import Stomp from "webstomp-client";
import SockJS from "sockjs-client";
import {helper} from "./helper";
import {onRestore, offRestore, reportSuccess, reportFailure} from "./connectivity";

export default class StompClient {
    stomp;
    store;
    user;
    // Handles returned by stomp.subscribe(), so disconnect() can unsubscribe() each of them
    // instead of leaving them dangling on a socket nothing references anymore.
    subscriptions = [];
    reconnectTimer = null;

    constructor(user, store) {
        this.store = store;
        this.user = user;
        // Bound once so onRestore()/offRestore() reference the same function.
        this.restoreHandler = () => this.scheduleReconnect(0);

        this.connect();
        onRestore(this.restoreHandler);
    }

    // Single point that arms the reconnect - both the STOMP error callback and a connectivity
    // restore event go through here, so a heartbeat-triggered restore can never race the plain
    // 10-second retry into opening two sockets at once.
    scheduleReconnect(delay) {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
        }
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.reConnect();
        }, delay);
    }

    connect() {
        // A previous connection (e.g. from reConnect()) is still open at this point - close it
        // and drop its subscription handles before opening a new socket, so a flaky connection
        // doesn't accumulate orphaned SockJS sockets and duplicate dispatches into the store.
        if (this.stomp && this.stomp.connected) {
            this.stomp.disconnect();
        }
        this.subscriptions = [];
        this.stomp = Stomp.over(new SockJS(process.env.VUE_APP_WEBSOCKET_URL), {debug: process.env.VUE_APP_DEBUG_HTTP === 'true'});
        this.store.dispatch('stompConnect', this);
    }

    reConnect() {
        // new AWN().alert("Связь с сервером не установлена. Переподключение через 10 сек...");
        console.log('STOMP reconnect... ');
        this.connect();
        this.init();
    }

    // Tears down every topic subscription and closes the underlying socket - call this when
    // whatever owns the client (DefaultLayout) is destroyed, so a logout/login cycle doesn't
    // leave the old connection running forever alongside the new one.
    disconnect() {
        offRestore(this.restoreHandler);
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        this.subscriptions.forEach(subscription => {
            try {
                subscription.unsubscribe();
            } catch (e) {
                // already gone with the socket - nothing to clean up
            }
        });
        this.subscriptions = [];
        if (this.stomp && this.stomp.connected) {
            this.stomp.disconnect();
        }
    }

    init() {
        this.stomp.connect({'Authorization': 'Bearer ' + helper.ls.get('user').token}, () => {
            this.subscribeReminders(this.user);
            this.subscribeNewRequests(this.user);
            this.subscribeUpdateAccount(this.user);
            this.subscribeNewClients(this.user);
            this.subscribeNewVisits(this.user);
            this.subscribeNotifications(this.user);
            this.subscribeScoreUpdated(this.user);
            this.subscribeRequestUpdated(this.user);
            reportSuccess();
        }, (error) => {
            console.log('STOMP: ' + error);
            reportFailure();
            this.scheduleReconnect(10000);
            console.log('STOMP: Reconnecting in 10 seconds');
        });
    }

    subscribeNewRequests() {
        // eslint-disable-next-line no-unused-vars
        this.subscriptions.push(this.stomp.subscribe(`/user/requests/new`, (request) => {
            this.store.dispatch('newRequestReceived', JSON.parse(request.body));
        }));
    }

    subscribeNewClients() {
        // eslint-disable-next-line no-unused-vars
        this.subscriptions.push(this.stomp.subscribe(`/user/clients/new`, (request) => {
            this.store.dispatch('newClientReceived', JSON.parse(request.body));
        }));
    }

    subscribeReminders() {
        this.subscriptions.push(this.stomp.subscribe(`/user/reminders/remind`, (remind) => {
            this.store.dispatch('remindReceived', JSON.parse(remind.body));
        }));
    }

    subscribeUpdateAccount() {
        // eslint-disable-next-line no-unused-vars
        this.subscriptions.push(this.stomp.subscribe(`/user/accounts/update`, (request) => {
            this.store.dispatch('updateAccountReceived', JSON.parse(request.body));
        }));
    }

    subscribeNewVisits() {
        // eslint-disable-next-line no-unused-vars
        this.subscriptions.push(this.stomp.subscribe(`/user/visits/new`, (request) => {
            console.log(request)
            this.store.dispatch('newVisitReceived', JSON.parse(request.body));
        }));
    }

    subscribeNotifications() {
        // eslint-disable-next-line no-unused-vars
        this.subscriptions.push(this.stomp.subscribe(`/user/notifications/new`, (request) => {
            this.store.dispatch('notificationReceived', JSON.parse(request.body));
        }));
    }

    subscribeScoreUpdated() {
        // eslint-disable-next-line no-unused-vars
        this.subscriptions.push(this.stomp.subscribe(`/user/requests/score-updated`, (message) => {
            this.store.dispatch('scoreUpdated', JSON.parse(message.body));
        }));
    }

    subscribeRequestUpdated() {
        // eslint-disable-next-line no-unused-vars
        this.subscriptions.push(this.stomp.subscribe(`/user/requests/updated`, (message) => {
            this.store.dispatch('requestUpdated', JSON.parse(message.body));
        }));
    }
}
